/**
 * Étape 6 — Résultat du diagnostic CEE agricole.
 *
 * Affiche :
 *  - Le montant estimé des primes CEE (fourchette min–max)
 *  - La liste des travaux sélectionnés avec leur prime indicative
 *  - Les suggestions complémentaires basées sur le contexte (serres, élevage…)
 *  - Les fiches CEE officielles éligibles (codes AGRI-XX)
 *  - Warnings (PCAE complémentaire, etc.)
 *  - Prochaines étapes + CTA contact + reset
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Calculator,
  CheckCircle2,
  ChevronRight,
  FileText,
  HandCoins,
  HelpCircle,
  Phone,
  Receipt,
  RefreshCw,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { easeStripe } from '../../../lib/motion';
import { useAgriStore } from './useAgriStore';
import { computeAgriEstimation, fmtEuroAgri } from './compute';
import { TRAVAUX_AGRI_CATEGORIES } from './agri-config';

export function Step6Resultat() {
  const orientation = useAgriStore((s) => s.orientation);
  const serres = useAgriStore((s) => s.serres);
  const elevage = useAgriStore((s) => s.elevage);
  const stockage = useAgriStore((s) => s.stockage);
  const equipements = useAgriStore((s) => s.equipements);
  const travaux = useAgriStore((s) => s.travaux);
  const company = useAgriStore((s) => s.company);
  const leadId = useAgriStore((s) => s.leadId);
  const reset = useAgriStore((s) => s.reset);
  const setStep = useAgriStore((s) => s.setStep);

  const results = useMemo(
    () =>
      computeAgriEstimation({
        ...useAgriStore.getState(),
        orientation,
        serres,
        elevage,
        stockage,
        equipements,
        travaux,
      }),
    [orientation, serres, elevage, stockage, equipements, travaux],
  );

  const noResult = results.primeMax === 0;

  return (
    <div>
      {/* ── Confirmation lead + headline ──────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: easeStripe }}
      >
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-100 text-brand-700 ring-4 ring-brand-50">
            <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
          </span>
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-700">
            {/* « dossier transmis » uniquement si le lead a réellement été créé */}
            {leadId ? 'Diagnostic terminé · dossier transmis' : 'Diagnostic terminé'}
          </p>
        </div>

        <h2 className="mt-3 h-section">
          Vos primes CEE estimées
        </h2>

        <div className="mt-3 flex items-end gap-2">
          <p className="font-display text-4xl font-black tabular-nums leading-none text-ink md:text-5xl">
            {fmtEuroAgri(results.primeMin)}{' '}
            <span className="text-stone-300">–</span>{' '}
            {fmtEuroAgri(results.primeMax)}{' '}
            <span className="text-2xl font-bold text-stone-400 md:text-3xl">€</span>
          </p>
        </div>

        <p className="mt-2 text-[13px] text-stone-500">
          {company ? `Pour ${company.name} · ` : ''}
          {results.travauxSelectionnes.length}{' '}
          {results.travauxSelectionnes.length <= 1
            ? 'poste de travaux'
            : 'postes de travaux'}{' '}
          · estimation indicative basée sur le prix du marché CEE 2026
        </p>
      </motion.div>

      {/* ── Travaux sélectionnés ──────────────────────────── */}
      {results.travauxSelectionnes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4, ease: easeStripe }}
          className="mt-7 rounded-2xl border border-stone-200/70 bg-white p-5 shadow-[0_2px_12px_-4px_rgba(10,10,10,.06)]"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
            Travaux sélectionnés
          </p>
          <div className="mt-3 space-y-2.5">
            {results.travauxSelectionnes.map((t, idx) => {
              const cat = TRAVAUX_AGRI_CATEGORIES.find((c) => c.id === t.category);
              const Icon = cat?.icon ?? TrendingUp;
              const pct = Math.min(
                100,
                (t.primeMax / Math.max(1, results.primeMax)) * 100,
              );
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.15 + idx * 0.05,
                    duration: 0.3,
                    ease: easeStripe,
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-brand-500">
                        <Icon
                          className="h-3 w-3 text-white"
                          strokeWidth={2.2}
                        />
                      </div>
                      <p className="truncate text-[13px] font-semibold text-stone-800">
                        {t.label}
                      </p>
                    </div>
                    <p className="shrink-0 text-[13px] font-bold tabular-nums text-stone-900">
                      {fmtEuroAgri(t.primeMin)} – {fmtEuroAgri(t.primeMax)} €
                    </p>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-stone-100">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{
                        duration: 0.7,
                        delay: 0.25 + idx * 0.05,
                        ease: easeStripe,
                      }}
                      className="h-full rounded-full bg-brand-500"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* ── Suggestions complémentaires ────────────────────── */}
      {results.suggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4, ease: easeStripe }}
          className="mt-5 overflow-hidden rounded-2xl border border-accent-200/70 bg-gradient-to-br from-accent-50/60 via-white to-brand-50/40 p-5"
        >
          <div className="flex items-center gap-2">
            <Sparkles
              className="h-3.5 w-3.5 text-accent-700"
              strokeWidth={2.4}
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-accent-900">
              Recommandé selon votre profil
            </p>
          </div>
          <p className="mt-1 text-[12.5px] leading-relaxed text-stone-600">
            Travaux complémentaires détectés grâce à votre saisie infrastructure
            et équipements.
          </p>
          <ul className="mt-3 space-y-1.5">
            {results.suggestions.map((s) => (
              <li
                key={s.id}
                className="flex items-start gap-2.5 rounded-lg bg-white/70 px-3 py-2 ring-1 ring-inset ring-accent-200/50"
              >
                <ChevronRight
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-700"
                  strokeWidth={2.4}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-semibold text-ink">
                    {s.label}
                  </p>
                  <p className="mt-0.5 text-[11px] text-stone-500">
                    {s.raison}
                  </p>
                </div>
                <p className="shrink-0 text-[11.5px] font-bold tabular-nums text-accent-900">
                  +{fmtEuroAgri(s.primeMin)} – {fmtEuroAgri(s.primeMax)} €
                </p>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* ── Fiches CEE éligibles (détails résolus via API) ── */}
      {results.fichesEligibles.length > 0 && (
        <FichesEligiblesCard codes={results.fichesEligibles} />
      )}

      {/* ── Warnings ──────────────────────────────────────── */}
      {results.warnings.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
          <ul className="space-y-1.5">
            {results.warnings.map((w) => (
              <li
                key={w}
                className="flex items-start gap-2 text-[12px] text-amber-900"
              >
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Disclaimer source ─────────────────────────────── */}
      <div className="mt-5 flex items-start gap-2 rounded-xl border border-stone-200 bg-stone-50 p-3">
        <HelpCircle
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"
          strokeWidth={1.8}
        />
        <p className="text-[11.5px] leading-relaxed text-stone-500">
          Les montants affichés sont des fourchettes indicatives. Le montant
          final dépend des travaux retenus, des conditions techniques de chaque
          dossier et de la signature du contrat avec un acteur du programme CEE.
        </p>
      </div>

      {/* ── Récap + prochaines étapes ─────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4, ease: easeStripe }}
        className="mt-6 grid gap-3 sm:grid-cols-2"
      >
        <NextStepCard
          icon={Receipt}
          title="Pré-instruction du dossier"
          desc="Un conseiller agricole valide chaque fiche éligible (pièces, plafonds, calendrier de dépôt)."
        />
        <NextStepCard
          icon={Phone}
          title="Recontact sous 24 h ouvrées"
          desc="Au numéro renseigné, par notre cellule dédiée agriculteurs (zéro démarchage)."
        />
      </motion.div>

      {/* ── CTA ───────────────────────────────────────────── */}
      <div className="mt-7 flex flex-col gap-3 sm:flex-row">
        <Button asChild variant="brand" size="xl" className="flex-1 gap-2">
          <a href="/contact?source=diagnostic-cee-agri">
            <Phone className="h-4 w-4" />
            Parler à un conseiller maintenant
          </a>
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="xl"
          onClick={() => setStep('orientation')}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Modifier mes réponses
        </Button>
      </div>

      <button
        type="button"
        onClick={reset}
        className="mt-3 w-full text-center text-[12px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline"
      >
        Recommencer un nouveau diagnostic
      </button>

      {noResult && results.suggestions.length === 0 && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4">
          <HandCoins
            className="mt-0.5 h-5 w-5 shrink-0 text-stone-400"
            strokeWidth={1.7}
          />
          <p className="text-[12.5px] text-stone-600">
            Avec les éléments saisis, le diagnostic n'a pas identifié de prime
            CEE directe. Un conseiller peut étudier des dispositifs
            complémentaires (PCAE, FranceAgriMer, aides régionales).{' '}
            <a
              href="/contact?source=diagnostic-cee-agri"
              className="font-semibold text-brand-700 hover:underline"
            >
              Demander une étude personnalisée →
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

/* ─── Fiches CEE éligibles — enrichies depuis le catalogue ─── */

interface ResolvedFiche {
  code: string;
  label: string;
  sector: string;
  primeEstimate: string | null;
  calculable: boolean;
  url: string;
}

function FichesEligiblesCard({ codes }: { codes: string[] }) {
  const [resolved, setResolved] = useState<ResolvedFiche[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/cee/fiches/by-codes?codes=${encodeURIComponent(codes.join(','))}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json: { fiches?: ResolvedFiche[] } | null) => {
        if (!cancelled && json?.fiches) setResolved(json.fiches);
      })
      .catch(() => {
        /* fallback badges statiques */
      });
    return () => {
      cancelled = true;
    };
  }, [codes]);

  return (
    <div className="mt-5 rounded-2xl border border-stone-200/70 bg-white p-5">
      <div className="flex items-center gap-2">
        <FileText className="h-3.5 w-3.5 text-stone-400" strokeWidth={1.8} />
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">
          Dispositifs CEE éligibles
        </p>
      </div>

      {resolved ? (
        <div className="mt-3 space-y-1.5">
          {resolved.map((f) => (
            <a
              key={f.code}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 rounded-lg border border-stone-200/70 bg-stone-50/50 px-3 py-2 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
            >
              <span className="shrink-0 font-mono text-[10.5px] font-bold tracking-wider text-stone-500">
                {f.code}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] text-stone-700">
                {f.label}
              </span>
              {f.primeEstimate && (
                <span className="hidden shrink-0 text-[11px] font-semibold text-stone-500 sm:inline">
                  {f.primeEstimate}
                </span>
              )}
              <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-brand-700">
                {f.calculable && <Calculator className="h-3 w-3" strokeWidth={2} />}
                {f.calculable ? 'Simuler' : 'Voir'}
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {codes.map((code) => (
            <span
              key={code}
              className="inline-flex items-center rounded-md bg-stone-100 px-2 py-1 font-mono text-[11px] font-bold tracking-wider text-stone-700 ring-1 ring-inset ring-stone-200/70"
            >
              {code}
            </span>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-stone-500">
        Source :{' '}
        <a
          href="https://www.ecologie.gouv.fr/politiques-publiques/operations-standardisees-deconomies-denergie"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-brand-700 hover:underline"
        >
          ecologie.gouv.fr · opérations standardisées CEE
        </a>
      </p>
    </div>
  );
}

function NextStepCard({
  icon: Icon,
  title,
  desc,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <Icon className="h-4 w-4" strokeWidth={1.7} />
      </span>
      <p className="mt-2.5 text-[13.5px] font-semibold text-ink">{title}</p>
      <p className="mt-0.5 text-[12px] leading-relaxed text-stone-500">{desc}</p>
    </div>
  );
}
